import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getInvoice, markInvoiceAsPaid } from "../services/invoiceService";
import { Invoice } from "../types";
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Button, Chip, CircularProgress, Paper, Alert } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import InvoicePDFDialog from "../components/InvoicePDFDialog";
import InvoiceEmailDialog from "../components/InvoiceEmailDialog";
import { Download as DownloadIcon, Email as EmailIcon } from "@mui/icons-material";

const InvoiceDetail: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  // Check if user is superuser
  const isSuperUser = user?.role === 'superuser';

  useEffect(() => {
    if (id) {
      getInvoice(Number(id)).then(data => {
        setInvoice(data as Invoice);
        setLoading(false);
      });
    }
  }, [id]);

  const handleMarkPaid = async () => {
    if (!invoice) return;
    setPaying(true);
    try {
      await markInvoiceAsPaid(invoice.id);
      const updated = await getInvoice(invoice.id);
      setInvoice(updated as Invoice);
      setSuccess("Invoice marked as paid successfully");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to mark invoice as paid");
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (!invoice) return <Typography>Invoice not found.</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>Back</Button>
      
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="h5">Invoice #{invoice.invoice_number}</Typography>
          {isSuperUser && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => setPdfDialogOpen(true)}
              >
                Generate PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => setEmailDialogOpen(true)}
              >
                Send Email
              </Button>
            </Box>
          )}
        </Box>
        
        {isSuperUser && <Typography>School: {invoice.school_name || invoice.school_id}</Typography>}
        <Typography>Status: <Chip label={invoice.status} color={invoice.status === "paid" ? "success" : invoice.status === "overdue" ? "error" : "warning"} /></Typography>
        <Typography>Due: {new Date(invoice.due_date).toLocaleDateString()}</Typography>
        <Typography>Issued: {new Date(invoice.issue_date).toLocaleDateString()}</Typography>
        <Typography>Amount: {invoice.currency} {invoice.total_amount.toLocaleString()}</Typography>
        {invoice.notes && <Typography>Notes: {invoice.notes}</Typography>}
        {invoice.status !== "paid" && isSuperUser && (
          <Button variant="contained" color="success" sx={{ mt: 2 }} onClick={handleMarkPaid} disabled={paying}>
            {paying ? "Marking..." : "Mark as Paid"}
          </Button>
        )}
      </Paper>
      <Typography variant="h6">Items</Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Description</TableCell>
            <TableCell>Qty</TableCell>
            <TableCell>Unit Price</TableCell>
            <TableCell>Total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoice.items.map(item => (
            <TableRow key={item.id}>
              <TableCell>{item.description}</TableCell>
              <TableCell>{item.quantity}</TableCell>
              <TableCell>{invoice.currency} {item.unit_price.toLocaleString()}</TableCell>
              <TableCell>{invoice.currency} {item.total_price.toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* PDF Dialog */}
      {isSuperUser && (
        <InvoicePDFDialog
          open={pdfDialogOpen}
          onClose={() => setPdfDialogOpen(false)}
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoice_number}
        />
      )}

      {/* Email Dialog */}
      {isSuperUser && (
        <InvoiceEmailDialog
          open={emailDialogOpen}
          onClose={() => setEmailDialogOpen(false)}
          invoiceId={invoice.id}
          invoiceNumber={invoice.invoice_number}
        />
      )}
    </Box>
  );
};

export default InvoiceDetail; 